import { Controller } from "@hotwired/stimulus";

/**
 * 編集と表示を切り替える
 */
export class EditableController extends Controller {
  static targets = ["edit", "display", "input"];
  static values = { editing: Boolean };
  static outlets = [
    // キャンセル時に確認メッセージを表示するモーダル
    "confirm",
  ];

  connect() {
    this.#saveInputStates();
    this.#update();
  }

  /** 表示から編集に切り替える */
  edit() {
    this.editingValue = true;
    this.#update();
  }

  /** 編集から表示に切り替える */
  finish() {
    this.editingValue = false;
    this.#update();
  }

  /** 編集中の値をリセットする */
  reset() {
    this.#loadInputStates();
  }

  /** 確認ダイアログでOKを押したら編集から表示に切り替える */
  async cancel() {
    if (this.hasConfirmOutlet) {
      if (await this.confirmOutlet.show()) {
        this.editingValue = false;
      }
    } else {
      this.editingValue = false;
    }
    this.#update();
  }

  /** 確認ダイアログでOKを押したら編集中の値をリセットして表示に切り替える */
  async abort() {
    if (this.hasConfirmOutlet) {
      if (await this.confirmOutlet.show()) {
        this.reset();
        this.editingValue = false;
      }
    } else {
      this.reset();
      this.editingValue = false;
    }
    this.#update();
  }

  #update() {
    for (const target of this.editTargets) {
      target.hidden = !this.editingValue;
      for (const input of target.querySelectorAll("input, select, textarea")) {
        // 初期状態を保存する
        if (input.dataset.defaultDisabled == null) {
          input.dataset.defaultDisabled = input.disabled;
        }
        if (this.editingValue) {
          input.disabled = getOriginalInput(input).dataset.defaultDisabled === "true";
        } else {
          input.disabled = true;
        }
      }
    }
    for (const target of this.displayTargets) {
      target.hidden = this.editingValue;
    }
  }

  // フォームの入力値を保存する
  #inputStates = new Map();

  #saveInputStates() {
    for (const input of this.inputTargets) {
      this.#saveInputState(input);
    }
  }

  #saveInputState(input) {
    this.#inputStates.set(input, { value: input.value, checked: input.checked });
  }

  #loadInputStates() {
    for (const input of this.inputTargets) {
      this.#loadInputState(input);
    }
  }

  #loadInputState(input) {
    const state = this.#inputStates.get(input);
    if (!state) return;
    const changed = input.value !== state.value || input.checked !== state.checked;
    input.value = state.value;
    input.checked = state.checked;
    if (changed) {
      input.dispatchEvent(new Event("change"));
    }
  }
}

/**
 * flatpickrはモバイルではinputを複製するため、元のinputを取得する。
 * flatpickrでない場合はそのまま返す。
 */
function getOriginalInput(input) {
  // copied: flatpickr-input flatpickr-mobile
  // original: flatpickr-input
  if (input.classList.contains("flatpickr-mobile")) {
    const original = input.previousElementSibling;
    if (original && original.classList.contains("flatpickr-input")) {
      return original;
    }
  }
  return input;
}
